/**
 * Phase 4A private user-library writes and Firestore-first item reads.
 *
 * Persists YouTube import observations under the signed-in user's library
 * only. My Music reads playlist items from Firestore; it does not write the
 * global catalog or run playlist sync.
 */
import {
  collection,
  doc,
  getDoc,
  getDocFromCache,
  getDocsFromCache,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  writeBatch,
} from 'firebase/firestore';
import {
  createUserPlaylistItemIdentity,
  providerAssetKey,
  userPlaylistId,
} from '../catalog/ids';
import {
  DATA_CLASSES,
  retentionPolicyFor,
} from '../catalog/lifecycle';
import {
  ASSET_KINDS,
  AVAILABILITY_STATES,
  DISPLAY_NAME_SOURCES,
  PLAYLIST_KINDS,
  SYNC_STATUSES,
  buildUserPlaylist,
  buildUserPlaylistItem,
  buildUserPrivateProviderAsset,
  normalizeUserPlaylistItemForRead,
} from '../catalog/schema';
import { db } from '../firebase';
import { recordLibraryMetric } from './libraryMetrics';
import { formatDurationMs } from './youtubeDuration';
import { YOUTUBE_READONLY_SCOPE } from './youtubeAuth';
import {
  derivePlaybackEligibility,
  isPlayableProviderSource,
  resolveLastKnownGoodObservation,
  toAssetLastKnownGoodObservation,
} from '../catalog/playbackEligibility';

export const LIBRARY_WRITE_BATCH_LIMIT = 400;
export const LIBRARY_VISIBLE_PAGE_TARGET = 200;
export const LIBRARY_FETCH_PAGE_LIMIT = 220;
export const UNAVAILABLE_REFRESHING_LABEL = 'Unavailable — refreshing';

export class UserLibraryReadError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'UserLibraryReadError';
    this.code = 'library-read';
    this.cause = cause;
  }
}

const PROVIDER = 'youtube';
const YOUTUBE_POLICY = retentionPolicyFor(
  DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
);

const AVAILABILITY_VALUES = new Set(Object.values(AVAILABILITY_STATES));

function requireUid(uid) {
  if (typeof uid !== 'string' || uid.trim().length === 0) {
    throw new TypeError('uid must be a nonempty string');
  }
  return uid;
}

function nonemptyString(value) {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : null;
}

function resolveObservedAt(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (value && typeof value.toMillis === 'function') {
    return new Date(value.toMillis());
  }
  return new Date();
}

function asTimestampOrNull(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted
      : null;
  }
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? new Date(millis) : null;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : new Date(parsed);
  }
  return null;
}

function addPolicyInterval(observedAt, intervalMs) {
  if (!(observedAt instanceof Date) || !Number.isSafeInteger(intervalMs)) {
    return null;
  }
  return new Date(observedAt.getTime() + intervalMs);
}

function youtubeLifecycle(observedAt) {
  return {
    dataClass: DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
    obtainedVia: 'oauth_user_grant',
    grantScope: YOUTUBE_READONLY_SCOPE,
    lastFetchedAt: observedAt,
    lastRefreshedAt: observedAt,
    refreshDueAt: addPolicyInterval(
      observedAt,
      YOUTUBE_POLICY.refreshIntervalMs,
    ),
    expiresAt: addPolicyInterval(observedAt, YOUTUBE_POLICY.hardExpiryMs),
  };
}

function trackVideoId(track) {
  return nonemptyString(track?.videoId) || nonemptyString(track?.id);
}

function trackOccurrenceIdentity(track) {
  const providerPlaylistItemId = nonemptyString(track?.playlistItemId)
    || nonemptyString(track?.providerPlaylistItemId);
  const providerOccurrenceId = nonemptyString(track?.providerOccurrenceId);

  if (!providerPlaylistItemId && !providerOccurrenceId) {
    throw new TypeError(
      'playlist item requires providerPlaylistItemId or providerOccurrenceId',
    );
  }

  return {
    ...createUserPlaylistItemIdentity({
      provider: PROVIDER,
      providerPlaylistItemId,
      providerOccurrenceId,
    }),
    providerPlaylistItemId,
  };
}

function integerDurationMs(track) {
  if (Number.isSafeInteger(track?.durationMs)) {
    return track.durationMs;
  }
  if (Number.isSafeInteger(track?.providerDurationMs)) {
    return track.providerDurationMs;
  }
  return null;
}

function trackAvailability(track) {
  if (AVAILABILITY_VALUES.has(track?.availability)) {
    return track.availability;
  }

  const title = track?.providerTitle || track?.title || '';
  if (title === 'Private video') return AVAILABILITY_STATES.PRIVATE;
  if (title === 'Deleted video') return AVAILABILITY_STATES.DELETED;
  return AVAILABILITY_STATES.AVAILABLE;
}

export function isPlayableImportedTrack(track) {
  return isPlayableProviderSource({
    ...track,
    availability: trackAvailability(track),
    providerAssetId: trackVideoId(track) || track?.providerAssetId,
  });
}

function trackPlaybackEligibility(track, observedAt) {
  const availability = trackAvailability(track);
  const embeddable = track?.playbackEligibility?.embeddable ?? track?.embeddable;
  const embeddableKnown = embeddable === true || embeddable === false
    || embeddable === 'true' || embeddable === 'false';
  return derivePlaybackEligibility({
    availability,
    embeddable,
    checkedAt: track?.playbackEligibility?.checkedAt
      ?? track?.embeddableCheckedAt
      ?? (embeddableKnown ? observedAt : null),
    reason: track?.playbackEligibility?.reason
      ?? track?.playbackEligibilityReason
      ?? null,
  });
}

function channelTitle(track) {
  return nonemptyString(track?.providerChannelTitle)
    || nonemptyString(track?.providerOwnerChannelTitle)
    || nonemptyString(track?.videoOwnerChannelTitle)
    || nonemptyString(track?.channelTitle)
    || nonemptyString(track?.artist)
    || null;
}

function itemObservation(track, observedAt) {
  const channel = channelTitle(track);
  return {
    providerTitle: nonemptyString(track?.providerTitle)
      || nonemptyString(track?.title)
      || null,
    providerChannelId: nonemptyString(track?.providerChannelId)
      || nonemptyString(track?.channelId)
      || nonemptyString(track?.videoOwnerChannelId)
      || null,
    providerChannelTitle: channel,
    providerOwnerChannelTitle: nonemptyString(track?.providerOwnerChannelTitle)
      || nonemptyString(track?.videoOwnerChannelTitle)
      || channel,
    providerStructuredArtists: Array.isArray(track?.providerStructuredArtists)
      ? track.providerStructuredArtists
      : null,
    providerDurationMs: integerDurationMs(track),
    providerThumbnailUrl: nonemptyString(track?.providerThumbnailUrl)
      || nonemptyString(track?.thumbnail)
      || null,
    providerPublishedAt: asTimestampOrNull(track?.providerPublishedAt),
    observedAt,
  };
}

function playlistCounts(playlist, tracks) {
  const knownItemCount = Number.isSafeInteger(playlist?.itemCount)
    ? playlist.itemCount
    : tracks.length;
  const loadedItemCount = tracks.length;
  const playableItemCount = tracks.filter(isPlayableImportedTrack).length;

  return { knownItemCount, loadedItemCount, playableItemCount };
}

export function userLibraryPaths(uid) {
  const owner = requireUid(uid);
  return {
    playlist: (playlistId) => `users/${owner}/playlists/${playlistId}`,
    item: (playlistId, itemId) => (
      `users/${owner}/playlists/${playlistId}/items/${itemId}`
    ),
    asset: (assetKey) => `users/${owner}/assets/${assetKey}`,
  };
}

export function buildImportedUserPlaylist({
  playlist,
  tracks = [],
  observedAt,
} = {}) {
  const providerPlaylistId = nonemptyString(playlist?.playlistId)
    || nonemptyString(playlist?.providerPlaylistId);
  if (!providerPlaylistId) {
    throw new TypeError('playlist.playlistId is required');
  }

  const observed = resolveObservedAt(observedAt);
  const counts = playlistCounts(playlist, tracks);

  return buildUserPlaylist({
    userPlaylistId: userPlaylistId(PROVIDER, providerPlaylistId),
    kind: PLAYLIST_KINDS.PROVIDER_MIRROR,
    provider: PROVIDER,
    providerPlaylistId,
    providerOwnerChannelId: nonemptyString(playlist?.providerOwnerChannelId)
      || nonemptyString(playlist?.channelId)
      || null,
    providerSnapshot: {
      title: typeof playlist?.title === 'string' ? playlist.title : '',
      description: playlist?.description ?? null,
      thumbnailUrl: nonemptyString(playlist?.thumbnailUrl)
        || nonemptyString(playlist?.thumbnail)
        || null,
      itemCount: counts.knownItemCount,
      privacyStatus: playlist?.privacyStatus ?? null,
      etag: playlist?.etag ?? null,
      observedAt: observed,
    },
    displayName: typeof playlist?.title === 'string' ? playlist.title : '',
    displayNameSource: DISPLAY_NAME_SOURCES.PROVIDER,
    knownItemCount: counts.knownItemCount,
    loadedItemCount: counts.loadedItemCount,
    playableItemCount: counts.playableItemCount,
    availability: AVAILABILITY_STATES.AVAILABLE,
    sync: {
      status: SYNC_STATUSES.IDLE,
      syncRunId: null,
      syncRunSeq: 0,
      startedAt: null,
      completedAt: null,
      lastSuccessfulSyncAt: null,
      providerRevision: playlist?.etag ?? null,
      cursor: null,
      failureCode: null,
      sweepCompletedAt: null,
    },
    lease: null,
    lifecycle: youtubeLifecycle(observed),
    importedAt: observed,
    createdAt: observed,
    updatedAt: observed,
  });
}

export function buildImportedPlaylistItem({
  track,
  index = 0,
  observedAt,
  previous = null,
} = {}) {
  const providerAssetId = trackVideoId(track);
  if (!providerAssetId) {
    throw new TypeError('playlist item requires a YouTube video id');
  }

  const observed = resolveObservedAt(observedAt);
  const identity = trackOccurrenceIdentity(track);
  const position = Number.isSafeInteger(track?.position) && track.position >= 0
    ? track.position
    : index;
  const observation = itemObservation(track, observed);
  const availability = trackAvailability(track);

  return buildUserPlaylistItem({
    userPlaylistItemId: identity.userPlaylistItemId,
    occurrenceIdSource: identity.occurrenceIdSource,
    provider: PROVIDER,
    providerPlaylistItemId: identity.providerPlaylistItemId,
    providerAssetKey: providerAssetKey(PROVIDER, providerAssetId),
    providerAssetId,
    position,
    observation,
    availability,
    playbackEligibility: trackPlaybackEligibility(track, observed),
    lastKnownGoodObservation: resolveLastKnownGoodObservation({
      previousObservation: previous?.observation,
      previousLastKnownGood: previous?.lastKnownGoodObservation,
      nextObservation: observation,
    }),
    addedToPlaylistAt: track?.addedToPlaylistAt ?? previous?.addedToPlaylistAt ?? null,
    lastSeenSyncRunSeq: Number.isSafeInteger(track?.lastSeenSyncRunSeq)
      ? track.lastSeenSyncRunSeq
      : 0,
    removedFromPlaylistAt: track?.removedFromPlaylistAt === undefined
      ? null
      : track.removedFromPlaylistAt,
    lifecycle: youtubeLifecycle(observed),
    createdAt: previous?.createdAt ?? observed,
    updatedAt: observed,
  });
}

export function buildImportedUserProviderAsset({
  track,
  observedAt,
  previous = null,
} = {}) {
  const providerAssetId = trackVideoId(track);
  if (!providerAssetId) {
    throw new TypeError('provider asset requires a YouTube video id');
  }

  const observed = resolveObservedAt(observedAt);
  const observation = itemObservation(track, observed);
  const availability = trackAvailability(track);
  const assetObservation = {
    providerTitle: observation.providerTitle,
    providerChannelId: observation.providerChannelId,
    providerChannelTitle: observation.providerChannelTitle,
    providerStructuredArtists: observation.providerStructuredArtists,
    providerDurationMs: observation.providerDurationMs,
    providerThumbnailUrl: observation.providerThumbnailUrl,
    providerPublishedAt: observation.providerPublishedAt,
    providerCategoryId: nonemptyString(track?.providerCategoryId) || null,
    availability,
    observedAt: observed,
  };
  const previousIdentity = previous?.observation
    || previous?.lastKnownGoodObservation
    || null;
  const lastKnownGood = toAssetLastKnownGoodObservation(
    resolveLastKnownGoodObservation({
      previousObservation: previousIdentity,
      previousLastKnownGood: previous?.lastKnownGoodObservation,
      nextObservation: assetObservation,
    }),
    previous?.observation?.availability || AVAILABILITY_STATES.AVAILABLE,
  );

  return buildUserPrivateProviderAsset({
    providerAssetKey: providerAssetKey(PROVIDER, providerAssetId),
    provider: PROVIDER,
    providerAssetId,
    assetKind: ASSET_KINDS.VIDEO,
    observation: assetObservation,
    playbackEligibility: trackPlaybackEligibility(track, observed),
    lastKnownGoodObservation: lastKnownGood,
    firstObservedAt: previous?.firstObservedAt ?? observed,
    lastObservedAt: observed,
    observedVia: ['playlist_import'],
    lifecycle: youtubeLifecycle(observed),
    createdAt: previous?.createdAt ?? observed,
    updatedAt: observed,
  });
}

function validateImportedTracks(tracks) {
  if (!Array.isArray(tracks)) {
    throw new TypeError('tracks must be an array');
  }

  tracks.forEach((track, index) => {
    if (!trackVideoId(track)) {
      throw new TypeError(`tracks[${index}] is missing a YouTube video id`);
    }
    trackOccurrenceIdentity(track);
  });
}

export function planImportedPlaylistWrites({
  uid,
  playlist,
  tracks,
  observedAt,
} = {}) {
  const owner = requireUid(uid);
  validateImportedTracks(tracks);
  const observed = resolveObservedAt(observedAt);
  const playlistDoc = buildImportedUserPlaylist({
    playlist,
    tracks,
    observedAt: observed,
  });
  const items = tracks.map((track, index) => buildImportedPlaylistItem({
    track,
    index,
    observedAt: observed,
  }));
  const assetsByKey = new Map();
  tracks.forEach((track) => {
    const asset = buildImportedUserProviderAsset({
      track,
      observedAt: observed,
    });
    assetsByKey.set(asset.providerAssetKey, asset);
  });
  const assets = [...assetsByKey.values()];
  const paths = userLibraryPaths(owner);

  const writes = [
    {
      uid: owner,
      path: paths.playlist(playlistDoc.userPlaylistId),
      segments: ['playlists', playlistDoc.userPlaylistId],
      data: playlistDoc,
    },
    ...items.map((item) => ({
      uid: owner,
      path: paths.item(playlistDoc.userPlaylistId, item.userPlaylistItemId),
      segments: [
        'playlists',
        playlistDoc.userPlaylistId,
        'items',
        item.userPlaylistItemId,
      ],
      data: item,
    })),
    ...assets.map((asset) => ({
      uid: owner,
      path: paths.asset(asset.providerAssetKey),
      segments: ['assets', asset.providerAssetKey],
      data: asset,
    })),
  ];

  return {
    uid: owner,
    playlistId: playlistDoc.userPlaylistId,
    providerPlaylistId: playlistDoc.providerPlaylistId,
    playlist: playlistDoc,
    items,
    assets,
    writes,
    playlistDocs: 1,
    itemDocs: items.length,
    assetDocs: assets.length,
    writeCount: writes.length,
    batchCount: writes.length === 0
      ? 0
      : Math.ceil(writes.length / LIBRARY_WRITE_BATCH_LIMIT),
  };
}

export async function commitUserLibraryWrites(writes, options = {}) {
  return commitLibraryWrites(options.firestore || db, writes);
}

export async function commitUserLibraryDeletes(deletes, options = {}) {
  return commitLibraryDeletes(options.firestore || db, deletes);
}

async function commitLibraryWrites(firestore, writes) {
  let batchCount = 0;

  for (let index = 0; index < writes.length; index += LIBRARY_WRITE_BATCH_LIMIT) {
    const chunk = writes.slice(index, index + LIBRARY_WRITE_BATCH_LIMIT);
    const batch = writeBatch(firestore);
    chunk.forEach((write) => {
      batch.set(
        doc(firestore, 'users', write.uid, ...write.segments),
        write.data,
        { merge: true },
      );
    });
    // Sequential commits keep a failed chunk from looking like a full persist.
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
    batchCount += 1;
  }

  return batchCount;
}

async function commitLibraryDeletes(firestore, deletes) {
  let batchCount = 0;
  const rows = Array.isArray(deletes) ? deletes : [];

  for (let index = 0; index < rows.length; index += LIBRARY_WRITE_BATCH_LIMIT) {
    const chunk = rows.slice(index, index + LIBRARY_WRITE_BATCH_LIMIT);
    const batch = writeBatch(firestore);
    chunk.forEach((entry) => {
      batch.delete(doc(firestore, 'users', entry.uid, ...entry.segments));
    });
    // Sequential commits keep a failed chunk from looking like a full delete.
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
    batchCount += 1;
  }

  return batchCount;
}

export async function persistUserPlaylist(uid, playlist, options = {}) {
  const owner = requireUid(uid);
  const tracks = options.tracks || [];
  if (tracks.length > 0) {
    validateImportedTracks(tracks);
  }
  const playlistDoc = buildImportedUserPlaylist({
    playlist,
    tracks,
    observedAt: options.observedAt,
  });
  const writes = [{
    uid: owner,
    path: userLibraryPaths(owner).playlist(playlistDoc.userPlaylistId),
    segments: ['playlists', playlistDoc.userPlaylistId],
    data: playlistDoc,
  }];
  const batchCount = await commitLibraryWrites(
    options.firestore || db,
    writes,
  );
  return {
    uid: owner,
    playlistId: playlistDoc.userPlaylistId,
    playlist: playlistDoc,
    writes,
    playlistDocs: 1,
    itemDocs: 0,
    assetDocs: 0,
    writeCount: 1,
    batchCount,
  };
}

export async function persistPlaylistItems(uid, playlist, tracks, options = {}) {
  const plan = planImportedPlaylistWrites({
    uid,
    playlist,
    tracks,
    observedAt: options.observedAt,
  });
  const writes = plan.writes.filter((write) => (
    write.segments[0] === 'playlists' && write.segments[2] === 'items'
  ));
  const batchCount = await commitLibraryWrites(
    options.firestore || db,
    writes,
  );
  return {
    ...plan,
    writes,
    writeCount: writes.length,
    batchCount,
    playlistDocs: 0,
    assetDocs: 0,
  };
}

export async function persistUserProviderAssets(uid, tracks, options = {}) {
  const owner = requireUid(uid);
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return {
      uid: owner,
      assets: [],
      writes: [],
      writeCount: 0,
      batchCount: 0,
      assetDocs: 0,
    };
  }

  tracks.forEach((track, index) => {
    if (!trackVideoId(track)) {
      throw new TypeError(`tracks[${index}] is missing a YouTube video id`);
    }
  });
  const observed = resolveObservedAt(options.observedAt);
  const assetsByKey = new Map();
  tracks.forEach((track) => {
    const asset = buildImportedUserProviderAsset({
      track,
      observedAt: observed,
    });
    assetsByKey.set(asset.providerAssetKey, asset);
  });
  const assets = [...assetsByKey.values()];
  const paths = userLibraryPaths(owner);
  const writes = assets.map((asset) => ({
    uid: owner,
    path: paths.asset(asset.providerAssetKey),
    segments: ['assets', asset.providerAssetKey],
    data: asset,
  }));
  const batchCount = await commitLibraryWrites(
    options.firestore || db,
    writes,
  );

  return {
    uid: owner,
    assets,
    writes,
    writeCount: writes.length,
    batchCount,
    assetDocs: assets.length,
  };
}

export async function persistImportedPlaylist(uid, playlist, tracks, options = {}) {
  const plan = planImportedPlaylistWrites({
    uid,
    playlist,
    tracks,
    observedAt: options.observedAt,
  });
  const batchCount = await commitLibraryWrites(
    options.firestore || db,
    plan.writes,
  );
  return { ...plan, batchCount };
}

export function resolveCatalogPlaylistId(playlistOrId) {
  if (typeof playlistOrId === 'string') {
    const raw = playlistOrId.trim();
    if (!raw) {
      throw new TypeError('playlistId is required');
    }
    return raw.includes(':') ? raw : userPlaylistId(PROVIDER, raw);
  }

  if (nonemptyString(playlistOrId?.userPlaylistId)) {
    return playlistOrId.userPlaylistId;
  }

  const provider = nonemptyString(playlistOrId?.provider) || PROVIDER;
  const raw = nonemptyString(playlistOrId?.providerPlaylistId)
    || nonemptyString(playlistOrId?.playlistId);
  if (!raw) {
    throw new TypeError('playlistId is required');
  }
  return raw.includes(':') ? raw : userPlaylistId(provider, raw);
}

export function isSoftRemovedLibraryItem(item) {
  return item?.removedFromPlaylistAt != null
    || item?.availability === AVAILABILITY_STATES.REMOVED;
}

export function isPlayableLibraryItem(item) {
  return Boolean(
    item
    && !isSoftRemovedLibraryItem(item)
    && isPlayableProviderSource(item),
  );
}

function structuredArtistName(entry) {
  if (typeof entry === 'string') return nonemptyString(entry);
  if (entry && typeof entry === 'object') {
    return nonemptyString(entry.name) || nonemptyString(entry.displayName);
  }
  return null;
}

function formatStructuredArtists(value) {
  if (!Array.isArray(value)) return null;
  const names = value.map(structuredArtistName).filter(Boolean);
  return names.length > 0 ? names.join(', ') : null;
}

export function isProviderObservationExpired(lifecycle, now = new Date()) {
  const expiresAt = asTimestampOrNull(lifecycle?.expiresAt);
  if (!expiresAt) return false;
  const current = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();
  return current.getTime() >= expiresAt.getTime();
}

export function clearedProviderObservation(existing = {}, role = 'render_snapshot') {
  const previous = existing && typeof existing === 'object' ? existing : {};
  const cleared = {
    providerTitle: null,
    providerChannelId: null,
    providerChannelTitle: null,
    providerStructuredArtists: null,
    providerDurationMs: null,
    providerThumbnailUrl: null,
    providerPublishedAt: null,
    observedAt: previous.observedAt ?? null,
    observationRole: previous.observationRole || role,
  };
  if (previous.providerOwnerChannelTitle !== undefined || role === 'render_snapshot') {
    cleared.providerOwnerChannelTitle = null;
  }
  if (previous.providerCategoryId !== undefined || role === 'authoritative') {
    cleared.providerCategoryId = null;
  }
  if (previous.availability !== undefined) {
    cleared.availability = previous.availability;
  }
  return cleared;
}

/**
 * My Music subtitle only. Channel, uploader, playlist owner, and addedBy
 * are not artist identity (§9.6). Queue attribution stays on addedBy.
 * Phase 4A has no persisted resolved-artist field; only genuine
 * providerStructuredArtists may appear. Hard-expired observations have
 * no subtitle.
 */
export function libraryItemArtistSubtitle(item, now = new Date()) {
  if (isProviderObservationExpired(item?.lifecycle, now)) return null;
  return formatStructuredArtists(item?.observation?.providerStructuredArtists);
}

export function libraryItemDisplayTitle(item, now = new Date()) {
  if (isProviderObservationExpired(item?.lifecycle, now)) {
    return UNAVAILABLE_REFRESHING_LABEL;
  }
  return nonemptyString(item?.observation?.providerTitle)
    || UNAVAILABLE_REFRESHING_LABEL;
}

export function libraryItemToQueueTrack(item, now = new Date()) {
  const expired = isProviderObservationExpired(item?.lifecycle, now);
  const observation = item?.observation || {};
  const videoId = nonemptyString(item?.providerAssetId);
  const durationMs = expired
    ? null
    : (Number.isSafeInteger(observation.providerDurationMs)
      ? observation.providerDurationMs
      : null);
  const channelTitle = expired
    ? ''
    : (nonemptyString(observation.providerChannelTitle)
      || nonemptyString(observation.providerOwnerChannelTitle)
      || '');

  return {
    id: videoId,
    videoId,
    title: libraryItemDisplayTitle(item, now),
    // Queue/player compatibility only. Channel/uploader is not artist identity.
    artist: channelTitle,
    channelTitle,
    artistSubtitle: libraryItemArtistSubtitle(item, now),
    thumbnail: expired ? '' : (nonemptyString(observation.providerThumbnailUrl) || ''),
    duration: formatDurationMs(durationMs),
    durationMs,
    source: 'youtube-playlist',
    playlistItemId: nonemptyString(item?.providerPlaylistItemId),
    userPlaylistItemId: nonemptyString(item?.userPlaylistItemId),
    providerAssetKey: nonemptyString(item?.providerAssetKey),
    position: Number.isSafeInteger(item?.position) ? item.position : 0,
    availability: item?.availability || AVAILABILITY_STATES.UNKNOWN,
    playable: !expired && isPlayableLibraryItem(item),
  };
}

function playlistMetaFromSnapshot(snapshot) {
  if (!snapshot?.exists?.()) {
    return {
      exists: false,
      loadedItemCount: null,
      knownItemCount: null,
    };
  }
  const data = snapshot.data() || {};
  if (data.catalogSchemaVersion !== 1) {
    return {
      exists: false,
      loadedItemCount: null,
      knownItemCount: null,
    };
  }
  return {
    exists: true,
    loadedItemCount: Number.isSafeInteger(data.loadedItemCount) ? data.loadedItemCount : null,
    knownItemCount: Number.isSafeInteger(data.knownItemCount) ? data.knownItemCount : null,
  };
}

async function readPhase4PlaylistMeta(firestore, uid, catalogId) {
  const ref = doc(firestore, 'users', uid, 'playlists', catalogId);
  let snapshot = null;
  try {
    snapshot = await getDocFromCache(ref);
  } catch {
    snapshot = null;
  }
  if (!snapshot?.exists?.()) {
    snapshot = await getDoc(ref);
  }
  return playlistMetaFromSnapshot(snapshot);
}

/**
 * A cached page is usable only when Firestore's cache can prove the page is
 * complete. A short page without matching `loadedItemCount` is treated as an
 * incomplete local subset — never as end-of-playlist.
 */
export function isCachedItemPageTrustworthy({
  fetchedCount,
  fetchLimit,
  afterPosition = null,
  loadedItemCount = null,
} = {}) {
  if (!Number.isSafeInteger(fetchedCount) || fetchedCount < 0) return false;
  if (!Number.isSafeInteger(fetchLimit) || fetchLimit <= 0) return false;
  if (fetchedCount === fetchLimit) return true;
  if (!Number.isSafeInteger(loadedItemCount) || loadedItemCount < 0) return false;
  const start = Number.isSafeInteger(afterPosition) ? afterPosition + 1 : 0;
  const expectedRemaining = Math.max(0, loadedItemCount - start);
  return fetchedCount === Math.min(fetchLimit, expectedRemaining);
}

function pageNumberFromCursor(cursor, fetchLimit) {
  if (!cursor) return 1;
  if (Number.isSafeInteger(cursor.afterPosition) && Number.isSafeInteger(fetchLimit) && fetchLimit > 0) {
    return Math.floor((cursor.afterPosition + 1) / fetchLimit) + 1;
  }
  return null;
}

async function queryPlaylistItemPage(itemQuery, {
  fetchLimit,
  afterPosition,
  loadedItemCount,
} = {}) {
  let cacheAttempted = true;
  let cacheResult = 'miss';
  let snapshot = null;

  try {
    snapshot = await getDocsFromCache(itemQuery);
    cacheResult = 'hit';
  } catch {
    snapshot = null;
    cacheResult = 'miss';
  }

  const cachedCount = snapshot?.docs?.length ?? 0;
  const trustworthy = snapshot != null && isCachedItemPageTrustworthy({
    fetchedCount: cachedCount,
    fetchLimit,
    afterPosition,
    loadedItemCount,
  });

  if (trustworthy) {
    return {
      snapshot,
      readSource: 'cache',
      cacheAttempted,
      cacheResult: 'hit',
    };
  }

  if (cacheResult === 'hit') {
    cacheResult = 'untrusted';
  }

  try {
    snapshot = await getDocsFromServer(itemQuery);
  } catch (error) {
    throw new UserLibraryReadError('Failed to read playlist items', error);
  }

  return {
    snapshot,
    readSource: cacheResult === 'untrusted' ? 'fallback' : 'server',
    cacheAttempted,
    cacheResult,
  };
}

export async function readUserPlaylistItemsPage({
  uid,
  playlist,
  playlistId,
  cursor = null,
  pageSize = LIBRARY_FETCH_PAGE_LIMIT,
  firestore = db,
} = {}) {
  const startedMs = Date.now();
  const owner = requireUid(uid);
  const catalogId = resolveCatalogPlaylistId(playlist || playlistId);
  const fetchLimit = Number.isSafeInteger(pageSize) && pageSize > 0
    ? pageSize
    : LIBRARY_FETCH_PAGE_LIMIT;
  const isInitial = !cursor;

  const itemsRef = collection(
    firestore,
    'users',
    owner,
    'playlists',
    catalogId,
    'items',
  );
  const clauses = [orderBy('position')];
  if (cursor?.snapshot) {
    clauses.push(startAfter(cursor.snapshot));
  } else if (Number.isSafeInteger(cursor?.afterPosition)) {
    clauses.push(startAfter(cursor.afterPosition));
  }
  clauses.push(limit(fetchLimit));
  const itemQuery = query(itemsRef, ...clauses);

  let phase4PlaylistExists = false;
  let loadedItemCount = null;
  let knownItemCount = null;
  if (isInitial) {
    try {
      const meta = await readPhase4PlaylistMeta(firestore, owner, catalogId);
      if (meta.exists) phase4PlaylistExists = true;
      loadedItemCount = meta.loadedItemCount;
      knownItemCount = meta.knownItemCount;
    } catch (error) {
      throw new UserLibraryReadError('Failed to read playlist', error);
    }
  }

  const pageRead = await queryPlaylistItemPage(itemQuery, {
    fetchLimit,
    afterPosition: Number.isSafeInteger(cursor?.afterPosition) ? cursor.afterPosition : null,
    loadedItemCount: isInitial ? loadedItemCount : null,
  });

  const docs = pageRead.snapshot?.docs || [];
  const items = docs.map((entry) => normalizeUserPlaylistItemForRead({
    userPlaylistItemId: entry.id,
    ...entry.data(),
  }));

  const lastVisible = docs[docs.length - 1] || null;
  const lastPosition = lastVisible
    ? lastVisible.data()?.position ?? items[items.length - 1]?.position
    : null;
  const hasMore = docs.length === fetchLimit;
  const nextCursor = hasMore && lastVisible
    ? {
      afterPosition: lastPosition,
      snapshot: lastVisible,
    }
    : null;

  if (items.length > 0) phase4PlaylistExists = true;

  recordLibraryMetric({
    operation: isInitial ? 'playlist_open' : 'load_more',
    catalogPlaylistId: catalogId,
    pageNumber: pageNumberFromCursor(cursor, fetchLimit),
    pageLimit: fetchLimit,
    documentsReturned: items.length,
    source: pageRead.readSource,
    cacheAttempted: pageRead.cacheAttempted,
    cacheResult: pageRead.cacheResult,
    isInitial,
    elapsedMs: Date.now() - startedMs,
    assetFanOut: 0,
  });

  return {
    uid: owner,
    catalogPlaylistId: catalogId,
    items,
    tracks: items.filter(isPlayableLibraryItem).map(libraryItemToQueueTrack),
    nextCursor,
    hasMore,
    fetchedCount: items.length,
    loadedItemCount,
    knownItemCount,
    phase4PlaylistExists,
    source: 'firestore',
    readSource: pageRead.readSource,
  };
}
